import { z } from "zod";
import { cursorQuerySchema, dateOnlySchema, listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { blankToNull, hhmmSchema, queryBooleanSchema, csvArraySchema } from "./common.ts";
import { financialYear } from "./india.ts";
import { currencyCodeSchema, minorSchema, toMajorString, type CurrencyCode } from "./money.ts";

/**
 * COLLECTIONS — the money coming back in (PRD §7 Item 4, US-4.5 … US-4.7).
 *
 * A **payment** is a bank fact: a date, an amount, a mode and a reference (the
 * UTR or the cheque number). It is NOT an invoice settlement by itself — the
 * settlement is the set of **allocations** that point the money at documents.
 * That separation is what lets one NEFT credit clear four invoices, lets an
 * excess sit as client credit, and lets a mistake be reversed instead of edited
 * (principles §7.6: facts are immutable, corrections are reversals).
 *
 * TDS (PRD §5.3) rides along on the payment: the client keeps back tax and pays
 * the rest, so an invoice is settled when `received + tds (+ write-off) = total`.
 * The section is a **label**, never a hard-coded number — the Income-tax Act,
 * 2025 renumbers the old 194C/194J from 1 April 2026.
 *
 * Money masking: every `*Minor` field in a RESPONSE is nullable, because a
 * caller without `financials:view` gets `null` rather than a plausible zero.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const PAYMENT_MODES = ["upi", "neft", "rtgs", "imps", "cheque", "cash", "card", "other"] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];
export const paymentModeSchema = z.enum(PAYMENT_MODES);

export const PAYMENT_MODE_LABELS: Record<PaymentMode, string> = {
  upi: "UPI",
  neft: "NEFT",
  rtgs: "RTGS",
  imps: "IMPS",
  cheque: "Cheque",
  cash: "Cash",
  card: "Card",
  other: "Other",
};

/** Modes whose `reference` is a bank reference the finance team will reconcile. */
export const REFERENCED_PAYMENT_MODES = ["upi", "neft", "rtgs", "imps", "cheque"] as const;

export const AGEING_BUCKETS = ["not_due", "d1_30", "d31_60", "d61_90", "d90_plus"] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];
export const ageingBucketSchema = z.enum(AGEING_BUCKETS);

export const AGEING_BUCKET_LABELS: Record<AgeingBucket, string> = {
  not_due: "Not due",
  d1_30: "1–30 days",
  d31_60: "31–60 days",
  d61_90: "61–90 days",
  d90_plus: "90+ days",
};

/** Which bucket a receivable falls in. `days` is whole days past the due date. */
export function ageingBucketOf(days: number): AgeingBucket {
  if (days <= 0) return "not_due";
  if (days <= 30) return "d1_30";
  if (days <= 60) return "d31_60";
  if (days <= 90) return "d61_90";
  return "d90_plus";
}

/**
 * Days sales outstanding — ONE definition, used by the receivables cockpit and
 * the dashboard's DSO tile so the two always agree:
 *
 *   DSO = open receivables ÷ credit sales in the window × window days
 *
 * Both sides are GST-INCLUSIVE (a receivable includes the GST the client owes,
 * so the sales it is divided by must too), and credit notes are netted off the
 * sales. The dashboard used to divide gross receivables by billings net of GST,
 * which inflated DSO by exactly the GST rate (106 days against the cockpit's 90).
 */
export function daysSalesOutstanding(receivablesMinor: number, creditSalesMinor: number, windowDays: number): number | null {
  if (creditSalesMinor <= 0) return null;
  return Math.round(((receivablesMinor / creditSalesMinor) * windowDays + Number.EPSILON) * 10) / 10;
}

/**
 * How a POSTED document moves a client's statement of account. ONE definition,
 * used by the staff statement and the client portal's, so the two can never
 * disagree again (they did: the staff side booked a proforma as a credit and
 * the portal booked it as a charge).
 *
 *  · `charge`      INV, DBN, BOS — the client owes more (a debit)
 *  · `credit_note` CRN — the client owes less (a credit)
 *  · `advance`     RV — money received ahead of the invoice (a credit, shown as
 *                  a receipt). The voucher is settled when it issues and no
 *                  payment row exists for it, so this IS the receipt.
 *  · `refund`      RFV — an advance paid back (a debit)
 *  · `null`        PI — a proforma is a request for an advance, not a supply;
 *                  it never moves the balance.
 */
export type StatementMovement = "charge" | "credit_note" | "advance" | "refund";

export function statementMovementOf(docType: string): StatementMovement | null {
  switch (docType) {
    case "INV":
    case "DBN":
    case "BOS":
      return "charge";
    case "CRN":
      return "credit_note";
    case "RV":
      return "advance";
    case "RFV":
      return "refund";
    default:
      return null;
  }
}

/** +1 when the movement increases what the client owes, −1 when it reduces it. */
export function statementSignOf(movement: StatementMovement): 1 | -1 {
  return movement === "charge" || movement === "refund" ? 1 : -1;
}

export const STATEMENT_ENTRY_KINDS = ["opening", "invoice", "debit_note", "receipt", "tds", "credit_note", "write_off"] as const;
export type StatementEntryKind = (typeof STATEMENT_ENTRY_KINDS)[number];
export const statementEntryKindSchema = z.enum(STATEMENT_ENTRY_KINDS);

export const REMINDER_CHANNELS = ["email", "whatsapp", "internal"] as const;
export type ReminderChannel = (typeof REMINDER_CHANNELS)[number];
export const reminderChannelSchema = z.enum(REMINDER_CHANNELS);

export const REMINDER_TONES = ["friendly", "neutral", "firm", "final"] as const;
export type ReminderTone = (typeof REMINDER_TONES)[number];
export const reminderToneSchema = z.enum(REMINDER_TONES);

export const REMINDER_TRIGGERS = ["auto", "manual"] as const;
export type ReminderTrigger = (typeof REMINDER_TRIGGERS)[number];
export const reminderTriggerSchema = z.enum(REMINDER_TRIGGERS);

/** Why a client or a document is out of the cadence (PRD US-4.7). */
export const REMINDER_PAUSE_REASONS = ["disputed", "on_hold", "no_automatic_reminders", "other"] as const;
export type ReminderPauseReason = (typeof REMINDER_PAUSE_REASONS)[number];
export const reminderPauseReasonSchema = z.enum(REMINDER_PAUSE_REASONS);

export const REMINDER_PAUSE_SCOPES = ["client", "invoice"] as const;
export type ReminderPauseScope = (typeof REMINDER_PAUSE_SCOPES)[number];
export const reminderPauseScopeSchema = z.enum(REMINDER_PAUSE_SCOPES);

/** The merge fields every reminder template may use (PRD US-4.7). */
export const REMINDER_MERGE_FIELDS = ["client_name", "invoice_no", "amount_due", "due_date", "days_overdue", "pay_link", "upi_qr"] as const;
export type ReminderMergeField = (typeof REMINDER_MERGE_FIELDS)[number];

/** Replaces `{field}` with its value; an unknown field is left alone, so a typo is visible. */
export function renderReminderTemplate(template: string, values: Partial<Record<ReminderMergeField, string>>): string {
  return template.replace(/\{([a-z_]+)\}/g, (whole, key: string) => {
    const value = values[key as ReminderMergeField];
    return value === undefined ? whole : value;
  });
}

// ---------------------------------------------------------------------------
// UPI (PRD §5.4) — generated locally, no gateway in Phase 1
// ---------------------------------------------------------------------------

export interface UpiLinkInput {
  /** The agency's VPA, e.g. "leanagency@hdfcbank". */
  payeeVpa: string;
  payeeName: string;
  /** Amount in minor units; omitted from the link when null (an open-amount QR). */
  amountMinor: number | null;
  currency: CurrencyCode;
  /** Printed in the payer's app — the invoice number. */
  transactionNote: string;
}

/**
 * `upi://pay?pa=<VPA>&pn=<name>&am=<amount>&cu=INR&tn=<invoice no>` — exactly the
 * PRD §5.4 shape, in that order. The same string is the QR payload: a UPI QR is
 * nothing but this URI encoded.
 */
export function buildUpiDeepLink(input: UpiLinkInput): string {
  const params = [
    `pa=${encodeURIComponent(input.payeeVpa)}`,
    `pn=${encodeURIComponent(input.payeeName)}`,
    ...(input.amountMinor === null ? [] : [`am=${toMajorString(input.amountMinor, input.currency)}`]),
    `cu=${input.currency}`,
    `tn=${encodeURIComponent(input.transactionNote)}`,
  ];
  return `upi://pay?${params.join("&")}`;
}

// ---------------------------------------------------------------------------
// Financial-year quarters (the TDS register, PRD §5.3)
// ---------------------------------------------------------------------------

/** 1–4 for the Indian financial year (Q1 = Apr–Jun when the FY starts in April). */
export function fyQuarterOf(date: string, fyStartMonth = 4): number {
  const month = Number(date.slice(5, 7));
  const offset = (month - fyStartMonth + 12) % 12;
  return Math.floor(offset / 3) + 1;
}

/** "Q2 FY2026-27" — how the register and the CA's workbook label a quarter. */
export function fyQuarterLabel(financialYearLabel: string, quarter: number): string {
  return `Q${quarter} FY${financialYearLabel}`;
}

/** The FY label a payment date belongs to, e.g. "2026-27". */
export function fyOfDate(date: string, fyStartMonth = 4): string {
  return financialYear(`${date}T00:00:00Z`, fyStartMonth);
}

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

const paymentClientSchema = z.object({ id: uuidSchema, number: z.string(), displayName: z.string() });

/** One payment pointed at one document. Money is `null` without `financials:view`. */
export const paymentAllocationSchema = z.object({
  id: uuidSchema,
  paymentId: uuidSchema,
  invoiceId: uuidSchema,
  invoiceNumber: z.string().nullable(),
  invoiceDate: dateOnlySchema.nullable(),
  invoiceDueDate: dateOnlySchema.nullable(),
  /** Cash applied to the document. */
  amountMinor: minorSchema.nullable(),
  /** TDS applied to the document — it settles the invoice too (PRD §5.3). */
  tdsMinor: minorSchema.nullable(),
  createdAt: timestampSchema,
});
export type PaymentAllocationDto = z.infer<typeof paymentAllocationSchema>;

export const paymentSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  client: paymentClientSchema.nullable(),
  paymentDate: dateOnlySchema,
  /** Money that actually reached the bank. */
  amountMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  mode: paymentModeSchema,
  modeLabel: z.string(),
  /** UTR, cheque number or gateway reference. */
  reference: z.string().nullable(),
  tdsMinor: minorSchema.nullable(),
  /** Configurable section label, never a hard-coded section number (PRD §5.3). */
  tdsLabel: z.string().nullable(),
  bankChargesMinor: minorSchema.nullable(),
  /** `amountMinor + tdsMinor` — what this payment can settle in total. */
  settlesMinor: minorSchema.nullable(),
  allocatedMinor: minorSchema.nullable(),
  unappliedMinor: minorSchema.nullable(),
  allocatedTdsMinor: minorSchema.nullable(),
  unappliedTdsMinor: minorSchema.nullable(),
  notes: z.string().nullable(),
  provider: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  reversedAt: timestampSchema.nullable(),
  reversalReason: z.string().nullable(),
  recordedByUserId: uuidSchema.nullable(),
  recordedByName: z.string().nullable(),
  allocations: z.array(paymentAllocationSchema),
  customFields: z.record(z.string(), z.unknown()),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type PaymentDto = z.infer<typeof paymentSchema>;

export const paymentSummarySchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  clientName: z.string(),
  paymentDate: dateOnlySchema,
  amountMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  mode: paymentModeSchema,
  modeLabel: z.string(),
  reference: z.string().nullable(),
  tdsMinor: minorSchema.nullable(),
  unappliedMinor: minorSchema.nullable(),
  allocationCount: z.number().int(),
  reversedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type PaymentSummaryDto = z.infer<typeof paymentSummarySchema>;

export const paymentTotalsSchema = z.object({
  currency: currencyCodeSchema,
  receivedMinor: minorSchema.nullable(),
  tdsMinor: minorSchema.nullable(),
  unappliedMinor: minorSchema.nullable(),
  count: z.number().int(),
});
export type PaymentTotalsDto = z.infer<typeof paymentTotalsSchema>;

/** GET /api/v1/payments → offset page plus per-currency totals. */
export const paymentPageSchema = z.object({
  items: z.array(paymentSummarySchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  totals: z.array(paymentTotalsSchema),
});
export type PaymentPage = z.infer<typeof paymentPageSchema>;

export const creditBalanceSchema = z.object({
  clientId: uuidSchema,
  currency: currencyCodeSchema,
  balanceMinor: minorSchema.nullable(),
  updatedAt: timestampSchema,
});
export type CreditBalanceDto = z.infer<typeof creditBalanceSchema>;

// ---------------------------------------------------------------------------
// Payment inputs
// ---------------------------------------------------------------------------

/** One line of "put this much of the payment on that invoice". */
export const allocationInputSchema = z
  .object({
    invoiceId: uuidSchema,
    amountMinor: minorSchema.min(0),
    /** Defaults to 0 — TDS is allocated explicitly, never guessed. */
    tdsMinor: minorSchema.min(0).optional(),
  })
  .strict()
  .refine((a) => a.amountMinor + (a.tdsMinor ?? 0) > 0, { message: "Allocate something to the invoice.", path: ["amountMinor"] });
export type AllocationInput = z.input<typeof allocationInputSchema>;
export type AllocationData = z.output<typeof allocationInputSchema>;

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const paymentWritableShape = {
  clientId: uuidSchema,
  paymentDate: dateOnlySchema,
  amountMinor: minorSchema.min(1),
  currency: currencyCodeSchema,
  mode: paymentModeSchema,
  reference: blankToNull(z.string().max(120)),
  tdsMinor: minorSchema.min(0),
  tdsLabel: blankToNull(z.string().max(120)),
  bankChargesMinor: minorSchema.min(0),
  notes: blankToNull(z.string().max(2000)),
  provider: blankToNull(z.string().max(40)),
  providerPaymentId: blankToNull(z.string().max(120)),
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/payments (perm `payments:record`) → 201 Payment.
 *
 * `allocations` settles named invoices in one commit; `autoAllocate` settles the
 * client's oldest open invoices first (US-4.5), which is the default the finance
 * team edits rather than types. Anything left over becomes client credit.
 * An `Idempotency-Key` header is honoured inside the money transaction.
 */
export const createPaymentInputSchema = z
  .object({
    ...paymentWritableShape,
    reference: paymentWritableShape.reference.optional(),
    tdsMinor: paymentWritableShape.tdsMinor.optional(),
    tdsLabel: paymentWritableShape.tdsLabel.optional(),
    bankChargesMinor: paymentWritableShape.bankChargesMinor.optional(),
    notes: paymentWritableShape.notes.optional(),
    provider: paymentWritableShape.provider.optional(),
    providerPaymentId: paymentWritableShape.providerPaymentId.optional(),
    customFields: paymentWritableShape.customFields.optional(),
    currency: paymentWritableShape.currency.optional(),
    allocations: z.array(allocationInputSchema).max(200).optional(),
    autoAllocate: z.boolean().optional(),
  })
  .strict();
export type CreatePaymentInput = z.input<typeof createPaymentInputSchema>;
export type CreatePaymentData = z.output<typeof createPaymentInputSchema>;

/**
 * PATCH /api/v1/payments/:id (perm `payments:record`) → Payment. Merge patch.
 * Amount, TDS, currency and client may only change while nothing is allocated —
 * otherwise unallocate first (409). Every edit is audited (US-4.5).
 */
export const updatePaymentInputSchema = z
  .object({ ...paymentWritableShape, reversalReason: blankToNull(z.string().max(500)) })
  .partial()
  .strict();
export type UpdatePaymentInput = z.input<typeof updatePaymentInputSchema>;
export type UpdatePaymentData = z.output<typeof updatePaymentInputSchema>;

/** POST /api/v1/payments/:id/allocate (perm `payments:record`) → Payment. Over-application → CONFLICT. */
export const allocatePaymentInputSchema = z
  .object({
    allocations: z.array(allocationInputSchema).max(200).optional(),
    /** Oldest invoice first, up to whatever is unapplied (US-4.5 default). */
    autoAllocate: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Boolean(v.autoAllocate) || (v.allocations?.length ?? 0) > 0, {
    message: "Name the invoices to settle, or ask for the oldest-first default.",
    path: ["allocations"],
  });
export type AllocatePaymentInput = z.input<typeof allocatePaymentInputSchema>;
export type AllocatePaymentData = z.output<typeof allocatePaymentInputSchema>;

/** POST /api/v1/payments/:id/unallocate (perm `payments:record`) → Payment. Omit `invoiceIds` to release everything. */
export const unallocatePaymentInputSchema = z.object({ invoiceIds: z.array(uuidSchema).max(200).optional() }).strict();
export type UnallocatePaymentInput = z.input<typeof unallocatePaymentInputSchema>;
export type UnallocatePaymentData = z.output<typeof unallocatePaymentInputSchema>;

export const listPaymentsQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  invoiceId: uuidSchema.optional(),
  mode: csvArraySchema(paymentModeSchema, PAYMENT_MODES.length).optional(),
  currency: currencyCodeSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  /** Only payments with money still sitting as client credit. */
  unappliedOnly: queryBooleanSchema.optional(),
  includeReversed: queryBooleanSchema.optional(),
  sort: z.enum(["paymentDate", "amountMinor", "createdAt"]).optional(),
});
export type ListPaymentsQuery = z.infer<typeof listPaymentsQuerySchema>;

// ---------------------------------------------------------------------------
// Receivables: ageing and DSO (US-4.6)
// ---------------------------------------------------------------------------

export const ageingTotalsSchema = z.object({
  notDueMinor: minorSchema.nullable(),
  days1To30Minor: minorSchema.nullable(),
  days31To60Minor: minorSchema.nullable(),
  days61To90Minor: minorSchema.nullable(),
  days90PlusMinor: minorSchema.nullable(),
  totalMinor: minorSchema.nullable(),
  overdueMinor: minorSchema.nullable(),
});
export type AgeingTotalsDto = z.infer<typeof ageingTotalsSchema>;

export const clientAgeingSchema = z.object({
  clientId: uuidSchema,
  clientName: z.string(),
  currency: currencyCodeSchema,
  notDueMinor: minorSchema.nullable(),
  days1To30Minor: minorSchema.nullable(),
  days31To60Minor: minorSchema.nullable(),
  days61To90Minor: minorSchema.nullable(),
  days90PlusMinor: minorSchema.nullable(),
  totalMinor: minorSchema.nullable(),
  overdueMinor: minorSchema.nullable(),
  /** Unapplied money already received from this client, netting the exposure down. */
  creditMinor: minorSchema.nullable(),
  openInvoiceCount: z.number().int(),
  oldestDueDate: dateOnlySchema.nullable(),
  /** Whole days past due on the oldest open document. */
  maxDaysOverdue: z.number().int(),
  lastPaymentDate: dateOnlySchema.nullable(),
  lastReminderOn: dateOnlySchema.nullable(),
  remindersPaused: z.boolean(),
});
export type ClientAgeingDto = z.infer<typeof clientAgeingSchema>;

/** GET /api/v1/receivables/ageing (perm `invoices:read`) → the US-4.6 report. */
export const ageingReportSchema = z.object({
  asOf: dateOnlySchema,
  currency: currencyCodeSchema,
  rows: z.array(clientAgeingSchema),
  totals: ageingTotalsSchema,
  /** Credit sales inside the DSO window — the denominator. */
  creditSalesMinor: minorSchema.nullable(),
  dsoWindowDays: z.number().int(),
  /** Days sales outstanding, rounded to one decimal; null when nothing was billed. */
  dso: z.number().nullable(),
  generatedAt: timestampSchema,
});
export type AgeingReportDto = z.infer<typeof ageingReportSchema>;

export const ageingQuerySchema = z.object({
  asOf: dateOnlySchema.optional(),
  currency: currencyCodeSchema.optional(),
  clientId: uuidSchema.optional(),
  /** Window for the DSO denominator (default 90 days). */
  dsoWindowDays: z.coerce.number().int().min(7).max(730).default(90),
  /** Hide clients whose exposure is entirely not-yet-due. */
  overdueOnly: queryBooleanSchema.optional(),
});
export type AgeingQuery = z.infer<typeof ageingQuerySchema>;

// ---------------------------------------------------------------------------
// Statement of account (US-4.6)
// ---------------------------------------------------------------------------

export const statementEntrySchema = z.object({
  date: dateOnlySchema,
  kind: statementEntryKindSchema,
  reference: z.string().nullable(),
  description: z.string(),
  /** Increases what the client owes (an invoice). */
  debitMinor: minorSchema.nullable(),
  /** Reduces it (a receipt, TDS, a credit note). */
  creditMinor: minorSchema.nullable(),
  /** Running balance after this entry. */
  balanceMinor: minorSchema.nullable(),
  entityType: z.enum(["invoice", "payment"]).nullable(),
  entityId: uuidSchema.nullable(),
});
export type StatementEntryDto = z.infer<typeof statementEntrySchema>;

/** GET /api/v1/clients/:id/statement (perm `invoices:read`) → opening + movements = closing. */
export const statementSchema = z.object({
  clientId: uuidSchema,
  clientName: z.string(),
  currency: currencyCodeSchema,
  from: dateOnlySchema,
  to: dateOnlySchema,
  openingBalanceMinor: minorSchema.nullable(),
  invoicedMinor: minorSchema.nullable(),
  receivedMinor: minorSchema.nullable(),
  tdsMinor: minorSchema.nullable(),
  creditedMinor: minorSchema.nullable(),
  writtenOffMinor: minorSchema.nullable(),
  closingBalanceMinor: minorSchema.nullable(),
  entries: z.array(statementEntrySchema),
  /** The arithmetic check US-4.8 demands before anything is exported. */
  reconciles: z.boolean(),
  generatedAt: timestampSchema,
});
export type StatementDto = z.infer<typeof statementSchema>;

export const statementQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  currency: currencyCodeSchema.optional(),
});
export type StatementQuery = z.infer<typeof statementQuerySchema>;

// ---------------------------------------------------------------------------
// TDS register (PRD §5.3)
// ---------------------------------------------------------------------------

export const tdsEntrySchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  clientName: z.string(),
  financialYear: z.string(),
  quarter: z.number().int(),
  quarterLabel: z.string(),
  currency: currencyCodeSchema,
  amountMinor: minorSchema.nullable(),
  /** Reconciliation against the annual tax statement. */
  certificateReceived: z.boolean(),
  certificateDate: dateOnlySchema.nullable(),
  certificateNumber: z.string().nullable(),
  label: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type TdsEntryDto = z.infer<typeof tdsEntrySchema>;

export const tdsRegisterTotalsSchema = z.object({
  currency: currencyCodeSchema,
  amountMinor: minorSchema.nullable(),
  certifiedMinor: minorSchema.nullable(),
  pendingMinor: minorSchema.nullable(),
});
export type TdsRegisterTotalsDto = z.infer<typeof tdsRegisterTotalsSchema>;

/** GET /api/v1/tds/entries (perm `invoices:read`) → the per-client, per-quarter register. */
export const tdsRegisterSchema = z.object({
  items: z.array(tdsEntrySchema),
  totals: z.array(tdsRegisterTotalsSchema),
});
export type TdsRegisterDto = z.infer<typeof tdsRegisterSchema>;

export const tdsRegisterQuerySchema = z.object({
  clientId: uuidSchema.optional(),
  financialYear: z.string().max(9).optional(),
  quarter: z.coerce.number().int().min(1).max(4).optional(),
  certificateReceived: queryBooleanSchema.optional(),
});
export type TdsRegisterQuery = z.infer<typeof tdsRegisterQuerySchema>;

/** PATCH /api/v1/tds/entries/:id (perm `payments:record`) → the certificate flag and its details. */
export const updateTdsEntryInputSchema = z
  .object({
    certificateReceived: z.boolean(),
    certificateDate: dateOnlySchema.nullable(),
    certificateNumber: blankToNull(z.string().max(60)),
    label: blankToNull(z.string().max(120)),
    notes: blankToNull(z.string().max(1000)),
  })
  .partial()
  .strict();
export type UpdateTdsEntryInput = z.input<typeof updateTdsEntryInputSchema>;
export type UpdateTdsEntryData = z.output<typeof updateTdsEntryInputSchema>;

// ---------------------------------------------------------------------------
// Reminders (US-4.7)
// ---------------------------------------------------------------------------

export const reminderScheduleSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** Negative = before the due date, 0 = on it, positive = after it. */
  offsetDays: z.number().int(),
  tone: reminderToneSchema,
  channel: reminderChannelSchema,
  subjectTemplate: z.string(),
  bodyTemplate: z.string(),
  /** The +30 step: nobody emails the client, the owner is told instead. */
  escalateToOwner: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ReminderScheduleDto = z.infer<typeof reminderScheduleSchema>;

/** GET /api/v1/reminders/settings (perm `invoices:read`) → cadence + guardrails. */
export const reminderSettingsSchema = z.object({
  enabled: z.boolean(),
  /** No client message leaves the building between these wall-clock times (org timezone). */
  quietHoursStart: z.string(),
  quietHoursEnd: z.string(),
  maxPerClientPerDay: z.number().int(),
  senderName: z.string().nullable(),
  replyToEmail: z.string().nullable(),
  schedules: z.array(reminderScheduleSchema),
  mergeFields: z.array(z.string()),
  updatedAt: timestampSchema,
});
export type ReminderSettingsDto = z.infer<typeof reminderSettingsSchema>;

/** PATCH /api/v1/reminders/settings (perm `invoices:send`) → merge patch of the guardrails. */
export const updateReminderSettingsInputSchema = z
  .object({
    enabled: z.boolean(),
    quietHoursStart: hhmmSchema,
    quietHoursEnd: hhmmSchema,
    maxPerClientPerDay: z.number().int().min(1).max(5),
    senderName: blankToNull(z.string().max(120)),
    replyToEmail: blankToNull(z.string().max(254)),
  })
  .partial()
  .strict();
export type UpdateReminderSettingsInput = z.input<typeof updateReminderSettingsInputSchema>;
export type UpdateReminderSettingsData = z.output<typeof updateReminderSettingsInputSchema>;

export const reminderScheduleInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    offsetDays: z.number().int().min(-90).max(365),
    tone: reminderToneSchema,
    channel: reminderChannelSchema,
    subjectTemplate: z.string().trim().min(1).max(200),
    bodyTemplate: z.string().trim().min(1).max(4000),
    escalateToOwner: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type ReminderScheduleInput = z.input<typeof reminderScheduleInputSchema>;
export type ReminderScheduleData = z.output<typeof reminderScheduleInputSchema>;

/** PUT /api/v1/reminders/schedules (perm `invoices:send`) → the cadence, replaced as a whole. Duplicate offsets → CONFLICT. */
export const replaceReminderSchedulesInputSchema = z.object({ schedules: z.array(reminderScheduleInputSchema).max(12) }).strict();
export type ReplaceReminderSchedulesInput = z.input<typeof replaceReminderSchedulesInputSchema>;
export type ReplaceReminderSchedulesData = z.output<typeof replaceReminderSchedulesInputSchema>;

export const reminderLogSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  clientName: z.string(),
  scheduleId: uuidSchema.nullable(),
  sentOn: dateOnlySchema,
  channel: reminderChannelSchema,
  tone: reminderToneSchema,
  trigger: reminderTriggerSchema,
  offsetDays: z.number().int().nullable(),
  invoiceIds: z.array(uuidSchema),
  invoiceCount: z.number().int(),
  totalDueMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  subject: z.string(),
  body: z.string(),
  recipients: z.array(z.string()),
  activityId: uuidSchema.nullable(),
  createdAt: timestampSchema,
});
export type ReminderLogDto = z.infer<typeof reminderLogSchema>;

export const reminderLogQuerySchema = cursorQuerySchema.extend({
  clientId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});
export type ReminderLogQuery = z.infer<typeof reminderLogQuerySchema>;

/** POST /api/v1/reminders/send-now (perm `invoices:send`) → one message listing every overdue invoice. */
export const sendReminderInputSchema = z
  .object({
    clientId: uuidSchema,
    /** Limit the message to these documents; omit for everything open. */
    invoiceIds: z.array(uuidSchema).max(100).optional(),
    tone: reminderToneSchema.optional(),
  })
  .strict();
export type SendReminderInput = z.input<typeof sendReminderInputSchema>;
export type SendReminderData = z.output<typeof sendReminderInputSchema>;

export const sendReminderResultSchema = z.object({
  sent: z.boolean(),
  /** Why nothing went out: "paused", "nothing_due", "covered_by_credit", "already_sent_today", "no_recipient", "quiet_hours". */
  reason: z.string().nullable(),
  log: reminderLogSchema.nullable(),
});
export type SendReminderResultDto = z.infer<typeof sendReminderResultSchema>;

export const reminderPauseSchema = z.object({
  id: uuidSchema,
  scope: reminderPauseScopeSchema,
  clientId: uuidSchema.nullable(),
  invoiceId: uuidSchema.nullable(),
  reason: reminderPauseReasonSchema,
  note: z.string().nullable(),
  /** Null = until somebody lifts it. */
  until: dateOnlySchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ReminderPauseDto = z.infer<typeof reminderPauseSchema>;

/** POST /api/v1/reminders/pauses (perm `invoices:send`) → 201 Pause. Exactly one of clientId/invoiceId. */
export const createReminderPauseInputSchema = z
  .object({
    clientId: uuidSchema.optional(),
    invoiceId: uuidSchema.optional(),
    reason: reminderPauseReasonSchema,
    note: blankToNull(z.string().max(500)).optional(),
    until: dateOnlySchema.nullable().optional(),
  })
  .strict()
  .refine((v) => Boolean(v.clientId) !== Boolean(v.invoiceId), {
    message: "Pause a client or a document, not both.",
    path: ["clientId"],
  });
export type CreateReminderPauseInput = z.input<typeof createReminderPauseInputSchema>;
export type CreateReminderPauseData = z.output<typeof createReminderPauseInputSchema>;

export const listReminderPausesQuerySchema = z.object({ clientId: uuidSchema.optional(), invoiceId: uuidSchema.optional() });
export type ListReminderPausesQuery = z.infer<typeof listReminderPausesQuerySchema>;

// ---------------------------------------------------------------------------
// UPI deep link / QR (PRD §5.4)
// ---------------------------------------------------------------------------

/** GET /api/v1/invoices/:id/upi-qr (perms `invoices:read` + `financials:view`). */
export const upiQrSchema = z.object({
  invoiceId: uuidSchema,
  invoiceNumber: z.string().nullable(),
  payeeVpa: z.string(),
  payeeName: z.string(),
  amountMinor: minorSchema,
  amountMajor: z.string(),
  currency: currencyCodeSchema,
  transactionNote: z.string(),
  /** `upi://pay?...` — open it on a phone. */
  deepLink: z.string(),
  /** The string a UPI QR encodes; identical to the deep link by design. */
  qrPayload: z.string(),
  /** Ready-to-render SVG, so no client needs a QR library. */
  qrSvg: z.string(),
});
export type UpiQrDto = z.infer<typeof upiQrSchema>;

// ---------------------------------------------------------------------------
// Default cadence (PRD US-4.7) — seeded per workspace, editable afterwards
// ---------------------------------------------------------------------------

export interface DefaultReminderStep {
  name: string;
  offsetDays: number;
  tone: ReminderTone;
  channel: ReminderChannel;
  subjectTemplate: string;
  bodyTemplate: string;
  escalateToOwner: boolean;
}

export const DEFAULT_REMINDER_CADENCE: readonly DefaultReminderStep[] = [
  {
    name: "3 days before due",
    offsetDays: -3,
    tone: "friendly",
    channel: "email",
    subjectTemplate: "{invoice_no} falls due on {due_date}",
    bodyTemplate:
      "Hi {client_name},\n\nA gentle reminder that {invoice_no} for {amount_due} falls due on {due_date}.\n\nYou can pay by UPI: {upi_qr}\nOr view the invoice here: {pay_link}\n\nThank you.",
    escalateToOwner: false,
  },
  {
    name: "On the due date",
    offsetDays: 0,
    tone: "friendly",
    channel: "email",
    subjectTemplate: "{invoice_no} is due today",
    bodyTemplate:
      "Hi {client_name},\n\n{invoice_no} for {amount_due} is due today ({due_date}).\n\nPay by UPI: {upi_qr}\nInvoice: {pay_link}\n\nThank you.",
    escalateToOwner: false,
  },
  {
    name: "3 days overdue",
    offsetDays: 3,
    tone: "neutral",
    channel: "email",
    subjectTemplate: "{invoice_no} is {days_overdue} days overdue",
    bodyTemplate:
      "Hi {client_name},\n\n{invoice_no} for {amount_due} was due on {due_date} and is now {days_overdue} days overdue.\n\nPay by UPI: {upi_qr}\nInvoice: {pay_link}\n\nDo tell us if anything is holding it up.",
    escalateToOwner: false,
  },
  {
    name: "7 days overdue",
    offsetDays: 7,
    tone: "neutral",
    channel: "email",
    subjectTemplate: "Reminder: {amount_due} outstanding",
    bodyTemplate:
      "Hi {client_name},\n\n{amount_due} is outstanding on {invoice_no}, now {days_overdue} days past the due date of {due_date}.\n\nPay by UPI: {upi_qr}\nInvoice: {pay_link}",
    escalateToOwner: false,
  },
  {
    name: "15 days overdue (firmer)",
    offsetDays: 15,
    tone: "firm",
    channel: "email",
    subjectTemplate: "Action needed: {amount_due} overdue by {days_overdue} days",
    bodyTemplate:
      "Hi {client_name},\n\n{amount_due} on {invoice_no} is {days_overdue} days overdue. Please arrange payment, or let us know the expected date so we can plan.\n\nPay by UPI: {upi_qr}\nInvoice: {pay_link}",
    escalateToOwner: false,
  },
  {
    name: "30 days overdue (internal escalation)",
    offsetDays: 30,
    tone: "final",
    channel: "internal",
    subjectTemplate: "{client_name}: {amount_due} overdue by {days_overdue} days",
    bodyTemplate: "{client_name} still owes {amount_due} on {invoice_no}, {days_overdue} days past due. Please step in.",
    escalateToOwner: true,
  },
];

export const REMINDER_SETTINGS_DEFAULTS = {
  enabled: true,
  quietHoursStart: "20:00",
  quietHoursEnd: "08:00",
  maxPerClientPerDay: 1,
} as const;
